import React, { useEffect, useState } from 'react';
import { IntercomSettingsPreview, SettingValue } from '../../api/intercomSettings';
import { Modal } from '../ui/Modal';

const LABELS: Record<string, string> = {
  guest_pin_access_enabled: 'Guest PIN', allow_video_call: 'Video calls', allow_phone_call: 'Voice calls',
  face_access_enabled: 'Face Access', allow_door_unlock_in_call: 'In-call unlock', pin_access_enabled: 'Tenant PIN',
};
const display = (value: SettingValue | undefined): string => typeof value === 'boolean' ? (value ? 'Enabled' : 'Disabled') : value == null || value === '' ? 'Not set' : Array.isArray(value) ? value.join(', ') : String(value);

interface Props {
  open: boolean;
  onClose: () => void;
  onApply: (previewToken: string) => void | Promise<void>;
  baseline: Record<string, SettingValue>;
  changes: Record<string, SettingValue>;
  preview: IntercomSettingsPreview;
  saving?: boolean;
}

export const BulkApplyDialog: React.FC<Props> = ({ open, onClose, onApply, baseline, changes, preview, saving = false }) => {
  const [confirmed, setConfirmed] = useState(false);
  useEffect(() => { if (open) setConfirmed(false); }, [open, preview.preview_token]);
  const updates = preview.targets.filter((target) => target.result === 'update');
  const skipped = preview.targets.filter((target) => target.result === 'skip' || target.result === 'error');
  return (
    <Modal open={open} onClose={onClose} title="Apply settings to all intercoms" size="lg">
      <div className="intercom-bulk-review">
        <p><strong>{updates.length} intercoms will be updated</strong></p>
        <ul className="intercom-bulk-review__changes">
          {Object.entries(changes).map(([field, after]) => (
            <li key={field}><strong>{LABELS[field] ?? field.replace(/_/g, ' ')}:</strong> {display(baseline[field])} → {display(after)}</li>
          ))}
        </ul>
        <div className="intercom-bulk-review__targets">
          {preview.targets.map((target) => <span key={target.intercom_id} className={`target target--${target.result}`}>{target.name} · {target.result}</span>)}
        </div>
        {skipped.map((target) => <div key={target.intercom_id} role="alert">{target.name}: {(target.issues ?? target.errors ?? []).map((item) => item.remediation).join(' ')}</div>)}
        <label className="intercom-bulk-review__confirm">
          <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
          I understand these reviewed fields will change on every listed intercom.
        </label>
        <div className="intercom-bulk-review__actions">
          <button type="button" className="secondary" onClick={onClose}>This intercom only</button>
          <button type="button" disabled={!confirmed || saving || updates.length === 0} onClick={() => onApply(preview.preview_token)}>
            Apply reviewed changes to {updates.length} intercoms
          </button>
        </div>
      </div>
    </Modal>
  );
};
